import React, { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";

interface WizardStepBarProps {
  stepLabels: string[];
  stepCurrent: number;
  onStepClick?: (stepIndex: number) => void;
  /** Je li prvi korak zadovoljen — dok nije, dalje se ide samo kroz "Nastavi". */
  firstStepCompleted?: boolean;
  /**
   * Broj vodećih obaveznih koraka koji se moraju proći redom. Kad su prođeni, otključava
   * se slobodna navigacija. 0 = uvijek restriktivno.
   */
  mandatoryCount?: number;
}

/**
 * Traka koraka iznad čarobnjaka — isti prikaz kao kod unosa vozila, kontakta i tenanta:
 * prođeni koraci zeleni s kvačicom, trenutni plav, ostali sivi.
 */
const WizardStepBar: React.FC<WizardStepBarProps> = ({
  stepLabels,
  stepCurrent,
  onStepClick,
  firstStepCompleted = false,
  mandatoryCount = 0,
}) => {
  const freeNavigation =
    mandatoryCount > 0 && firstStepCompleted && stepCurrent >= mandatoryCount;

  return (
    <div className="w-50 flex border border-x-0 border-t-0 border-slate-200 overflow-hidden">
      {stepLabels.map((label, index) => {
        const isActive = index === stepCurrent;
        const isPast = index < stepCurrent;
        const isFirstStep = index === 0;

        // Dok se prolaze obavezni koraci: naprijed samo kroz "Nastavi" (validacija),
        // natrag na prođene je dozvoljeno.
        const isDisabled = freeNavigation
          ? false
          : isFirstStep ||
            (!firstStepCompleted && index > 0) ||
            index > stepCurrent;

        const isCompleted =
          isPast || (isActive && isFirstStep && firstStepCompleted);
        const isInProgress = isActive && !isCompleted;

        return (
          <button
            key={index}
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onStepClick?.(index)}
            className={classNames(
              "flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm border-r last:border-r-0 transition-all duration-200",
              isCompleted
                ? "bg-green-100 text-green-800 font-semibold border-b-2 border-b-green-600"
                : isInProgress
                  ? "bg-sky-50 text-sky-700 font-medium border-slate-200"
                  : "bg-white text-slate-400 font-medium border-slate-200",
              !isDisabled && "cursor-pointer hover:opacity-80"
            )}
          >
            {isCompleted && (
              <ReactSVG
                src="/media/icons/check.svg"
                className="w-4 h-4 stroke-green-700"
              />
            )}
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default memo(WizardStepBar);
